import React, { useState } from "react";
import {
    Grid,
    IconButton,
    Typography,
    Card,
    CardContent,
    Snackbar,
    Chip,
    Box,
    Tooltip,
    Collapse, Alert
} from "@mui/material";
import SpeedIcon from "@mui/icons-material/Speed";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import RestoreIcon from "@mui/icons-material/Restore";
import TuneIcon from "@mui/icons-material/Tune";
import SensitivityDrawer from "../drawers/SensitivityDrawer";

const Sensitivity = ({ initialThreshold, initialSensitivity, saveUrl, canEdit }) => {
    const [openDrawer, setOpenDrawer] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [threshold, setThreshold] = useState(initialThreshold ?? 15);
    const [sensitivity, setSensitivity] = useState(initialSensitivity ?? 85);
    const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });

    const handleSave = async ({ threshold, sensitivity }) => {
        const saveQueryUrl = `${saveUrl}&motion_threshold=${threshold}&motion_sensitivity=${sensitivity}`;
        try {
            const response = await fetch(saveQueryUrl);
            if (!response.ok) {
                let errorMessage = response.statusText;
                const errorData = await response.json?.();
                if (errorData?.message) {
                    errorMessage = errorData.message;
                }
                setAlert({ open: true, message: `Error: ${errorMessage}`, severity: 'error' });
            } else {
                const data = await response.json?.();
                setAlert({
                    open: true,
                    message: data?.message || 'Update successful!',
                    severity: 'success',
                    variant: "filled"
                });
            }
        } catch (error) {
            setAlert({ open: true, message: `Error: ${error.message}`, severity: 'error' });
        }
        setThreshold(threshold);
        setSensitivity(sensitivity);
    };

    const resetSensitivity = () => {
        const defaultThreshold = 15;
        const defaultSensitivity = 85;
        setThreshold(defaultThreshold);
        setSensitivity(defaultSensitivity);
        handleSave({ threshold: defaultThreshold, sensitivity: defaultSensitivity });
        setOpenDrawer(false);
    };

    return (
        <>
            <Card variant="outlined" sx={{ mb: 0, borderRadius: 2, width: '70%', float: 'right' }}>
                <CardContent>
                    <SensitivityDrawer
                        open={openDrawer}
                        onClose={() => setOpenDrawer(false)}
                        initialThreshold={threshold}
                        initialSensitivity={sensitivity}
                        onSave={handleSave}
                    />

                    {/* Header with toggle */}
                    <Grid container alignItems="center">
                        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle2">MOTION SETTINGS</Typography>
                            <IconButton onClick={() => setExpanded(prev => !prev)} size="small">
                                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                        </Grid>
                    </Grid>

                    {/* Collapsed View */}
                    {!expanded && (
                        <Grid container spacing={1} alignItems="center" sx={{ mt: 1 }}>
                            <Grid item xs={3} md={3} lg={12} xl={12}>
                                <Chip icon={<SignalCellularAltIcon />} label={threshold} size="small" />
                            </Grid>
                            <Grid item xs={3} md={3} lg={12} xl={12}>
                                <Chip icon={<SpeedIcon />} label={sensitivity} size="small" />
                            </Grid>
                            {canEdit && (
                                <>
                                    <Grid item xs={3} md={3} lg={12} xl={6}>
                                        <Tooltip title="Reset">
                                            <IconButton onClick={resetSensitivity} color="secondary" size="small">
                                                <RestoreIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                    <Grid item xs={3} md={3} lg={12} xl={6}>
                                        <Tooltip title="Edit">
                                            <IconButton onClick={() => setOpenDrawer(true)} color="primary" size="small">
                                                <TuneIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    )}

                    {/* Expanded View */}
                    <Collapse in={expanded}>
                        <Grid container spacing={2} sx={{ padding: '10px 0 0 0' }}>
                            <Grid item xs={12}>
                                <Typography variant="body1">Motion Threshold: {threshold}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="body1">Motion Sensitivity: {sensitivity}</Typography>
                            </Grid>
                            {canEdit && (
                                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'right' }}>
                                    <Tooltip title="Reset">
                                        <IconButton onClick={resetSensitivity} color="secondary">
                                            <RestoreIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Edit">
                                        <IconButton onClick={() => setOpenDrawer(true)} color="primary">
                                            <TuneIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                            )}
                        </Grid>
                    </Collapse>
                </CardContent>
            </Card>

            <Snackbar
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                open={alert.open}
                autoHideDuration={5000}
                onClose={() => setAlert({ ...alert, open: false })}
                message={alert.message}
            >
                <Alert
                    onClose={() => setAlert({ ...alert, open: false })}
                    severity={alert.severity}
                    variant={alert.variant}
                    sx={{ width: '100%' }}
                    >{alert.message}</Alert>
            </Snackbar>
        </>
    );
};

export default Sensitivity;
